import { combineReducers, AnyAction as BaseAction } from 'redux';
import { AppDispatch, AppState, AppThunkDispatch } from 'src/store';

import service from 'src/ServiceContainer';
import { ASSORTMENT, DIMENSION_PRODUCT, LEVEL_STYLE_COLOR } from 'src/utils/Domain/Constants';
import { getGroupBySelectedOptionProperty, getSortBySelectedOptionProperty } from 'src/utils/Pivot/Sort';
import { ListDataOptions, Pivot } from 'src/worker/pivotWorker.types';
import { concat, get, isEmpty, isNil, omit } from 'lodash';
import { receiveError as shareDataError } from './SharedData.slice';
import { CacheCheckResponse } from 'src/services/pivotService';

import canvasViewReducer from './CanvasView/CanvasView.slice';
import summaryViewReducer from './SummaryView/SummaryView.slice';
import topTYvsLYReducer from './TopTYvsLY/TopTYvsLY.slice';
import flowTypeReducer from './FlowType/FlowType.slice';
import gridViewReducer from './GridView/GridView.slice';
import collectionViewReducer from './CollectionView/CollectionView.slice';
import sharedDataReducer from './SharedData.slice';
import { isListDataApi, DataApi } from 'configuration-schemas/confdefnView';
import { SubheaderSlice } from 'src/components/Subheader/Subheader.slice';
import { BasicTenantConfigViewItem } from 'src/dao/tenantConfigClient';

const styleColorReviewSlice = combineReducers({
  sharedData: sharedDataReducer,
  collectionView: collectionViewReducer,
  canvasView: canvasViewReducer,
  summaryView: summaryViewReducer,
  gridView: gridViewReducer,
  flowType: flowTypeReducer,
  topTYvsLY: topTYvsLYReducer,
});
export interface StyleColorReviewSlice extends ReturnType<typeof styleColorReviewSlice> {}
export default styleColorReviewSlice;

//#region pivot fetch utility functions

export type ExtraPivotOptions = Record<string, string | number[] | undefined>;

function getDefaultAggBy(key: 'aggBy' | 'topAggBy', dataApi?: DataApi): string {
  return isListDataApi(dataApi) ? get(dataApi.params, key, LEVEL_STYLE_COLOR) : LEVEL_STYLE_COLOR;
}

export function getConfigureModalAggBys(currentAggBys: string[], dataApi?: DataApi): string {
  let defaultAggBy = '';
  let aggBys: string[] = [];

  if (isListDataApi(dataApi)) {
    if (dataApi.params?.topAggBy) {
      defaultAggBy = getDefaultAggBy('topAggBy', dataApi);
      aggBys = concat([defaultAggBy], currentAggBys);
    } else {
      defaultAggBy = getDefaultAggBy('aggBy', dataApi);
      aggBys = concat(currentAggBys, defaultAggBy);
    }
  } else {
    defaultAggBy = getDefaultAggBy('aggBy');
    aggBys = concat(currentAggBys, defaultAggBy);
  }

  return aggBys.filter((i) => !isEmpty(i)).join(',');
}

interface GetAggByOptions {
  /** If true, sets the value to the groupBy dropdown value instead of potentially resetting it to an empty string  */
  forceReload?: boolean;
  /** Allows the default bottom level to be ignored. */
  includeBottomLevel?: boolean;
  /** Allows for a dynamic value for retreiving the correct property value for groupingKey. Some views are not configured with `groupingKey` */
  groupingKeyOverride?: keyof BasicTenantConfigViewItem;
}

export function getAggBys(state: SubheaderSlice, dataApi?: DataApi, options?: GetAggByOptions): string {
  const { forceReload = false, includeBottomLevel = true, groupingKeyOverride = 'groupingKey' } = options || {};
  const subheaderGroupBy = state.groupBy;
  const groupingKey = getGroupBySelectedOptionProperty(subheaderGroupBy, groupingKeyOverride);
  const groupByDimension = getGroupBySelectedOptionProperty(subheaderGroupBy, 'dimension');
  let groupBy = '';

  if (forceReload) {
    groupBy = groupingKey;
  } else {
    groupBy = DIMENSION_PRODUCT !== groupByDimension || subheaderGroupBy.refreshOnChange ? groupingKey : '';
  }

  const defaultAggBy = getDefaultAggBy('aggBy', dataApi);
  return includeBottomLevel ? [groupBy, defaultAggBy].filter((i) => !isEmpty(i)).join(',') : groupBy;
}

export function getConfigureAggBys(state: SubheaderSlice, dataApi?: DataApi, options?: GetAggByOptions): string {
  const { includeBottomLevel = true } = options || {};
  const configureSelections = state.configureSelections;
  const groupBys: string[] =
    configureSelections
      ?.map((i) => i.groupingKey ?? i.dataIndex)
      .filter((i) => !isNil(i))
      .map((i) => {
        const keySplit = i.split(':');
        if (keySplit.length > 1) {
          if (keySplit[0] == 'member') return ['level', keySplit[1]].join(':');
          else return [keySplit[0], keySplit[1]].join(':');
        } else {
          return ['custom', keySplit[0]].join(':');
        }
      }) ?? [];

  const defaultAggBy = getDefaultAggBy('aggBy', dataApi);
  return includeBottomLevel
    ? concat(groupBys, defaultAggBy)
        .filter((i) => !isEmpty(i))
        .join(',')
    : groupBys.join(',');
}

/**
 * Combines a variety of inputs into a single object to be provided to the pivot request.
 */
export function organizeListDataOptions(extraOptions: ExtraPivotOptions, dataApi?: DataApi): ListDataOptions {
  if (isListDataApi(dataApi)) {
    if (!dataApi.isListData) {
      service.loggingService.warn(
        `Potentially malformed ClientDataApi detected, check UI Confdefn for 'isListData: false'`,
      );
      return {} as ListDataOptions;
    }

    // order matters here, anything in extraOptions will overwrite what is in dataApi.params.
    // if you want to take a configured value from dataApi.params (e.g. aggBy), just don't include it in extraOptions
    return {
      ...omit(dataApi.params, 'topAggBy'), // topAggBy is only used client side
      ...extraOptions,
    };
  }

  return extraOptions;
}

export function getSortBy(state: SubheaderSlice) {
  return getSortBySelectedOptionProperty(state.sortBy, 'dataIndex');
}

//#endregion

//#region pivot fetch functions

/**
 * Fetches cached data if possible and returns immediately to caller
 * while waiting for the live data load to be ready to return to caller.
 *
 * Allows caching of any pivot that is configured to return a cached/live data promise
 */
export function cacheCheckFetchPivotData(
  requestPromises: Promise<CacheCheckResponse>,
  requestData: () => BaseAction,
  receiveCacheHash: (cacheHash: string) => BaseAction,
  receiveCachedData: (cacheHash: string) => BaseAction,
  receiveLiveData: (cacheHash: string) => BaseAction,
) {
  return async (dispatch: AppThunkDispatch): Promise<BaseAction | void> => {
    dispatch(requestData());
    try {
      const { cacheHash, cachePromise, dataPromise } = await requestPromises;
      dispatch(receiveCacheHash(cacheHash));

      const { cacheHit } = await cachePromise;
      if (cacheHit) {
        dispatch(receiveCachedData(cacheHash));
      }

      await dataPromise;
      return dispatch(receiveLiveData(cacheHash));
    } catch (error) {
      return dispatch(shareDataError(error));
    }
  };
}

/**
 * @todo convert this method to be more 'generic' like above, rename to fetchPivotData
 *
 * A cacheless listData fetch
 *
 * Primarily used by overtime views (flowsheet, pricing, style over time, etc.) for
 * mass edit and overtime data fetches.
 */
export function fetchListData(
  modelDefn: string,
  requestData: () => BaseAction,
  receiveData: (data: Pivot) => BaseAction,
  options?: ListDataOptions,
) {
  return async (dispatch: AppThunkDispatch): Promise<BaseAction | void> => {
    dispatch(requestData());
    try {
      const resp = await service.pivotService.listData(modelDefn, ASSORTMENT, options);
      return dispatch(receiveData(resp));
    } catch (error) {
      return dispatch(shareDataError(error));
    }
  };
}

//#endregion

//#region deprecated pivot fetch functions

/**
 * @deprecated
 * DO NOT USE this function
 *
 * It is only used by Style Edit because converting to storing the cacheHash only breaks the lens update logic in StyleEdit.container.ts
 *
 * Fetches cached data if possible and returns immediately to caller
 * while waiting for the live data load to be ready to return to caller.
 */
export function cacheCheckFitViewData(
  modelDefn: string,
  requestData: () => BaseAction,
  receiveCachedData: (data: Pivot) => BaseAction,
  receiveLiveData: (data: Pivot) => BaseAction,
  receiveError: (error: any) => BaseAction,
  extraParams?: { [key: string]: string | undefined },
): (dispatch: AppThunkDispatch) => Promise<BaseAction | void> {
  return async (dispatch: AppThunkDispatch): Promise<BaseAction | void> => {
    dispatch(requestData());

    try {
      const { cachePromise, dataPromise } = await service.pivotService.fitViewCacheCheck(modelDefn, {
        ...extraParams,
      });

      const { cacheHit, cacheData } = await cachePromise;

      if (cacheHit) {
        dispatch(receiveCachedData(cacheData));
        const liveData = await dataPromise;
        return dispatch(receiveLiveData(liveData));
      } else {
        const liveData = await dataPromise;
        return dispatch(receiveLiveData(liveData));
      }
    } catch (error) {
      return dispatch(receiveError(error));
    }
  };
}

export interface CachePayload {
  data: Pivot;
  cacheHash: string;
}

/**
 * @deprecated
 * DO NOT USE this function
 *
 * It is only used by Worklist because converting to storing the cacheHash only breaks the lens update logic in Worklist.container.ts
 */
export function cacheCheckListData(
  modelDefn: string,
  requestData: () => BaseAction,
  receiveCacheHash: (cacheHash: string) => BaseAction,
  receiveCachedData: (data: CachePayload) => BaseAction,
  receiveLiveData: (data: CachePayload) => BaseAction,
  extraParams?: ListDataOptions,
) {
  return async (dispatch: AppDispatch, getState: () => AppState): Promise<BaseAction | void> => {
    const subheaderGroupBy = getState().subheader.groupBy;
    const groupingKey = getGroupBySelectedOptionProperty(subheaderGroupBy, 'groupingKey');
    const groupByDimension = getGroupBySelectedOptionProperty(subheaderGroupBy, 'dimension');
    const groupBys: string[] = [];
    if (DIMENSION_PRODUCT !== groupByDimension || subheaderGroupBy.refreshOnChange) {
      groupBys.push(groupingKey);
    }
    groupBys.push('level:stylecolor');
    const extraParamsToSend: ListDataOptions = isNil(extraParams)
      ? { aggBy: groupBys.join(',') }
      : {
          aggBy: groupBys.join(','),
          ...extraParams,
        };

    try {
      const { cachePromise, dataPromise, cacheHash } = await service.pivotService.listDataCacheCheck(
        modelDefn,
        extraParamsToSend,
      );
      dispatch(receiveCacheHash(cacheHash));
      const { cacheHit, cacheData } = await cachePromise;

      if (cacheHit) {
        dispatch(receiveCachedData({ data: cacheData, cacheHash }));
      } else {
        dispatch(requestData());
      }

      const liveData = await dataPromise;
      return dispatch(receiveLiveData({ data: liveData, cacheHash }));
    } catch (error) {
      return dispatch(shareDataError(error));
    }
  };
}

/**
 * @deprecated
 * DO NOT USE THIS FUNCTION
 *
 * It is a one-off for now used by Worklist over time views.
 * Changing this in `cacheCheckFetchPivotData` (which is used by every component)
 * changes the loading flow for all components.
 *
 * For now, isolate this behavior until ready to bring this logic to all components.
 */
export function worklistOverTimeFetchPivotData(
  requestPromises: Promise<CacheCheckResponse>,
  requestData: () => BaseAction,
  receiveCacheHash: (cacheHash: string) => BaseAction,
  receiveCachedData: (cacheHash: string) => BaseAction,
  receiveLiveData: (cacheHash: string) => BaseAction,
) {
  return async (dispatch: AppThunkDispatch): Promise<BaseAction | void> => {
    try {
      const { cacheHash, cachePromise, dataPromise } = await requestPromises;
      dispatch(receiveCacheHash(cacheHash));

      const { cacheHit } = await cachePromise;
      if (cacheHit) {
        dispatch(receiveCachedData(cacheHash));
      } else {
        dispatch(requestData());
      }

      await dataPromise;
      return dispatch(receiveLiveData(cacheHash));
    } catch (error) {
      return dispatch(shareDataError(error));
    }
  };
}

//#endregion
